/**
 * Pangolin scale converter: reference values and calculation.
 *
 * Source of the numbers (keep this note up to date when they change):
 *  - share / lower / upper: from the saved bootstrap results (boot_bbp_ci.rds,
 *    boot_sspp_ci.rds, boot_wbp_ci.rds, dated 10 Aug 2026). `share` is the
 *    bootstrap estimate (t0) of each species' share of seized scale mass;
 *    `lower`/`upper` are the 95% BCa confidence limits (bca[4] and bca[5]).
 *  - massPerIndividualG: dried mass of complete scales per individual, from
 *    the conversion factors in rscripts/converter.r.
 *
 * The calculation mirrors the end of rscripts/converter.r: share x total mass
 * / conversion factor, rounded to a whole number of individuals. The R script
 * remains the authoritative method; if it or its inputs change, update the
 * values below.
 */

interface SpeciesModel {
  share: number;
  lower: number;
  upper: number;
  massPerIndividualG: number;
}

const BLACK_BELLIED: SpeciesModel = {
  share: 0.0683572888269746,
  lower: 0.033570910041909,
  upper: 0.14549263979903,
  massPerIndividualG: 198.91,
};

const SMUTSIA: SpeciesModel = {
  share: 0.25925271939458,
  lower: 0.160957385257884,
  upper: 0.362085836822687,
  massPerIndividualG: 2935,
};

const WHITE_BELLIED: SpeciesModel = {
  share: 0.668039262962183,
  lower: 0.553487684067012,
  upper: 0.765589306107737,
  massPerIndividualG: 177.38,
};

export interface IndividualsEstimate {
  lower: number;
  estimate: number;
  upper: number;
}

export interface ConversionResult {
  blackBellied: IndividualsEstimate;
  smutsia: IndividualsEstimate;
  whiteBellied: IndividualsEstimate;
}

// R rounds exact halves to the nearest even number; Math.round rounds them up.
const roundLikeR = (x: number): number => {
  const floor = Math.floor(x);
  return x - floor === 0.5 ? (floor % 2 === 0 ? floor : floor + 1) : Math.round(x);
};

const estimateIndividuals = (species: SpeciesModel, totalMassG: number): IndividualsEstimate => ({
  lower: roundLikeR((species.lower * totalMassG) / species.massPerIndividualG),
  estimate: roundLikeR((species.share * totalMassG) / species.massPerIndividualG),
  upper: roundLikeR((species.upper * totalMassG) / species.massPerIndividualG),
});

export const convertScalesToIndividuals = (totalMassG: number): ConversionResult => ({
  blackBellied: estimateIndividuals(BLACK_BELLIED, totalMassG),
  smutsia: estimateIndividuals(SMUTSIA, totalMassG),
  whiteBellied: estimateIndividuals(WHITE_BELLIED, totalMassG),
});
